import type { OnboardingDecisions, OnboardingDetails, WorkerScenario, WorkerOrgScenario } from "@/api/onboarding-api.types"
import type { Placement, PlacementFormState } from "@/components/ats-fbo/types"
import { PlacementStatus } from "@/components/ats-fbo/types"
import { CODE_VALUE_GROUPS } from "@/types/code-values"

// ---------------------------------------------------------------------------
// Code value lookup interfaces (injected so mappers stay pure)
// ---------------------------------------------------------------------------

type GetCodeValueCode = (id: number, groupName: string) => string
type GetCodeValueIdByCode = (code: string, groupName: string) => number | null

export interface PlacementMapperLookups {
  getCodeValueCode: GetCodeValueCode
}

export interface ReverseMapperLookups {
  getCodeValueIdByCode: GetCodeValueIdByCode
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function numToStr(value: number | null | undefined): string | null {
  return value != null ? String(value) : null
}

function strToNum(value: string | null | undefined): number | null {
  if (value == null) return null
  const trimmedValue = value.trim()
  if (trimmedValue === "") return null
  const n = Number(trimmedValue)
  return Number.isNaN(n) ? null : n
}

function strToFloat(value: string | null | undefined): number | null {
  if (value == null) return null
  const trimmedValue = value.trim()
  if (trimmedValue === "") return null
  const numericLiteralPattern = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/
  if (!numericLiteralPattern.test(trimmedValue)) return null
  const n = Number(trimmedValue)
  return Number.isNaN(n) ? null : n
}

function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return ""
  return iso.slice(0, 10) || ""
}

// ---------------------------------------------------------------------------
// OnboardingDetails (API) → Placement (UI)
// ---------------------------------------------------------------------------

export function mapOnboardingDetailsToPlacement(
  details: OnboardingDetails,
  lookups: PlacementMapperLookups,
  idDisplayText: string,
): Placement {
  const captured  = details.placement   // AtsPlacementSnapshot — same role as capturedAtsData
  const decisions = details.decisions   // OnboardingDecisions
  const worker    = details.worker      // AtsWorkerSnapshot

  // Internal company and client org both fall back to a captured FBO id like LOB does. Neither
  // organizationIdInternal nor clientOrganizationFboId is an ATS id — both are resolved upstream
  // (the ATS field-mapping and etl.AtsMappingClientOrganization respectively) — so when the user
  // hasn't overridden them we pre-select directly, and useOrganizationSearch passes the selected id
  // into the search for BOTH roles, so the dropdown resolves its own label either way.
  //
  // clientOrgId used to be decisions-only, which forced every request through the name-based
  // auto-match below. That match is exact, so an ATS "Acme Corp (ACME)" never matched FBO's
  // "Acme Corp" and the reviewer was told the client org had to be created — for a client that was
  // already mapped. The auto-match stays as the fallback for clients with no resolved id.
  const internalCompanyId = decisions?.internalCompanyId ?? captured?.organizationIdInternal ?? null
  const clientOrgId = decisions?.clientOrgId ?? captured?.clientOrganizationFboId ?? null
  const workerId = decisions?.worker?.kind === "existing-profile" ? decisions.worker.profileId : null
  const workerContactId = decisions?.worker?.contactId ?? null
  // Same shape as clientOrgId above: workerOrganizationFboId is resolved upstream, so pre-select it when
  // the reviewer has made no worker-org decision yet. Skipping the pre-select lets them create a second
  // organization for a vendor that already has one.
  const workerOrgId = decisions?.workerOrganization?.kind === "existing"
    ? decisions.workerOrganization.organizationId
    : (decisions?.workerOrganization?.kind == null ? (captured?.workerOrganizationFboId ?? null) : null)
  const profileTypeCode = decisions?.profileTypeCode ?? worker?.profileTypeCode ?? captured?.workerProfileTypeCode ?? null
  const templateId = decisions?.templateId ?? null

  // LOB: convert numeric ID to code. Producers that only have the code on
  // hand (e.g. VMS — the LLM extracts "SubVendorPayroll" from the email but
  // doesn't have access to tenant code-table IDs) set lineOfBusinessCode and
  // leave lineOfBusinessId null. We honor that as a fallback so the form's
  // LOB dropdown still pre-selects the right value — the dropdown is keyed
  // off the code string regardless of whether it arrived via id-lookup or
  // direct from the producer.
  const lobIdNumeric = decisions?.lineOfBusinessId ?? captured?.lineOfBusinessId ?? null
  const lobCode = lobIdNumeric != null
    ? lookups.getCodeValueCode(lobIdNumeric, CODE_VALUE_GROUPS.LINE_OF_BUSINESS) || null
    : (captured?.lineOfBusinessCode ?? null)

  // ATS source LOB code (from captured data only). Same fallback shape so
  // VMS-sourced sessions show their LOB hint in the captured-data column.
  const atsLobCode = captured?.lineOfBusinessId != null
    ? lookups.getCodeValueCode(captured.lineOfBusinessId, CODE_VALUE_GROUPS.LINE_OF_BUSINESS) || null
    : (captured?.lineOfBusinessCode ?? null)

  const startDate = formatDateOnly(decisions?.startDate ?? captured?.startDate)
  const endDate = formatDateOnly(decisions?.endDate ?? captured?.endDate)
  const billRate = decisions?.billRate ?? captured?.primaryBillingRate ?? null
  const payRate = decisions?.payRate ?? captured?.primaryPaymentRate ?? null
  const billCurrencyCode = decisions?.billCurrencyCode ?? captured?.billingCurrency ?? null
  const payCurrencyCode = decisions?.payCurrencyCode ?? captured?.paymentCurrency ?? null

  const workerEmail = decisions?.workerEmail ?? worker?.primaryEmail ?? ""

  const atsId = idDisplayText
  const workerFullName = worker?.fullName
    ?? [worker?.firstName, worker?.lastName].filter(Boolean).join(" ")
    ?? ""

  return {
    id: details.id,
    atsId,
    internalCompanyId: numToStr(internalCompanyId),
    clientOrgId: numToStr(clientOrgId),
    workerId: numToStr(workerId),
    workerContactId: numToStr(workerContactId),
    workerOrgId: numToStr(workerOrgId),
    workerEmail,
    lobCode,
    profileTypeCode,
    startDate,
    endDate,
    billRate,
    payRate,
    billCurrencyCode,
    payCurrencyCode,
    templateV2Id: numToStr(templateId),
    workerPreferredLanguage: worker?.preferredLanguage
      ?? (decisions?.worker?.kind === "create-contact" ? decisions.worker.preferredLanguage ?? null : null),
    dateCreated: formatDateOnly(details.createdAt),
    status: PlacementStatus.PENDING,
    atsSource: {
      workerName: workerFullName,
      workerEmail: worker?.primaryEmail ?? "",
      clientOrgName: captured?.clientDisplayName ?? "",
      internalCompanyName: null,
      profileTypeCode: captured?.workerProfileTypeCode ?? null,
      workerOrgName: captured?.supplier?.legalName ?? null,
      lobCode: atsLobCode,
      startDate: formatDateOnly(captured?.startDate),
      endDate: formatDateOnly(captured?.endDate),
      billRate: captured?.primaryBillingRate ?? null,
      payRate: captured?.primaryPaymentRate ?? null,
      billCurrencyCode: captured?.billingCurrency ?? null,
      payCurrencyCode: captured?.paymentCurrency ?? null,
    },
  }
}

// ---------------------------------------------------------------------------
// PlacementFormState (UI) → OnboardingDecisions (API)
// ---------------------------------------------------------------------------

export function mapFormStateToOnboardingDecisions(
  form: PlacementFormState,
  lookups: ReverseMapperLookups,
): OnboardingDecisions {
  const lineOfBusinessId = form.lobCode
    ? lookups.getCodeValueIdByCode(form.lobCode, CODE_VALUE_GROUPS.LINE_OF_BUSINESS)
    : null

  let worker: WorkerScenario | null = null
  if (form.workerScenario) {
    switch (form.workerScenario.kind) {
      case "existing-profile":
        worker = {
          kind: "existing-profile",
          profileId: strToNum(form.workerScenario.profileId),
          contactId: strToNum(form.workerScenario.contactId),
        }
        break
      case "add-profile":
        worker = {
          kind: "add-profile",
          contactId: strToNum(form.workerScenario.contactId),
        }
        break
      case "create-contact":
        // PreferredLanguage rides inside the WorkerScenario for create-contact only — it's
        // a per-contact attribute applied at contact creation time. The other kinds operate
        // on existing contacts whose language is canonical in FBO and must not be touched.
        worker = {
          kind: "create-contact",
          preferredLanguage: form.workerPreferredCultureCode || null,
        }
        break
    }
  }

  let workerOrganization: WorkerOrgScenario | null = null
  if (form.workerOrgScenario) {
    switch (form.workerOrgScenario.kind) {
      case "existing":
        workerOrganization = { kind: "existing", organizationId: strToNum(form.workerOrgScenario.organizationId) }
        break
      case "new":
        workerOrganization = { kind: "new", organizationName: form.workerOrgScenario.organizationName }
        break
      case "none":
        workerOrganization = { kind: "none" }
        break
    }
  }

  return {
    internalCompanyId: strToNum(form.internalCompanyId),
    clientOrgId: strToNum(form.clientOrgId),
    profileTypeCode: form.profileTypeCode ?? null,
    workerEmail: form.workerEmail || null,
    worker,
    workerOrganization,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    billRate: strToFloat(form.billRate),
    payRate: strToFloat(form.payRate),
    billCurrencyCode: form.billCurrencyCode ?? null,
    payCurrencyCode: form.payCurrencyCode ?? null,
    lineOfBusinessId,
    templateId: strToNum(form.templateV2Id),
  }
}
